/**
 * ChatInput — Composer + STT mic, matching the v1.1 prototype.
 *
 * Layout: single rounded box (mic + auto-resizing textarea + send),
 * Sienna focus ring, mono hint line with keyboard shortcuts.
 */

import { useCallback, useEffect, useRef, type KeyboardEvent } from 'react';
import { Mic, MicOff, Send } from 'lucide-react';
import { cn } from '@/lib/utils';

const MAX_CHARS = 10000;

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** Whether the AI is currently processing a response */
  isWaiting: boolean;
  /** Whether STT is actively listening */
  isListening: boolean;
  /** Whether STT is supported */
  isSTTSupported: boolean;
  /** Current interim STT text */
  interimTranscript?: string;
  /** Toggle STT recording */
  onToggleSTT: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the entire input is disabled (e.g. interview complete) */
  disabled?: boolean;
}

export function ChatInput({
  value,
  onChange,
  onSubmit,
  isWaiting,
  isListening,
  isSTTSupported,
  interimTranscript,
  onToggleSTT,
  placeholder = 'Type your response…',
  disabled = false,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resizeTextarea = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [value, resizeTextarea]);

  const canSubmit = value.trim().length > 0 && !isWaiting && !disabled;
  const charCount = value.length;
  const isOverLimit = charCount > MAX_CHARS;

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSubmit) onSubmit();
    }
  };

  const effectivePlaceholder = isWaiting
    ? 'Kaiva is thinking…'
    : isListening
      ? 'Listening…'
      : placeholder;

  return (
    <div
      className="flex-shrink-0 px-[14px] py-[10px] pb-[14px]"
      style={{ background: 'hsl(var(--card))' }}
    >
      {/* Interim STT preview */}
      {isListening && interimTranscript && (
        <div
          className="mb-2 rounded-lg px-3 py-1.5 text-xs italic"
          style={{
            background: 'hsl(var(--muted))',
            color: 'hsl(var(--muted-foreground))',
          }}
        >
          {interimTranscript}
        </div>
      )}

      {/* Composer box */}
      <div
        className={cn(
          'flex items-end gap-1.5 rounded-xl transition-[border-color,box-shadow] duration-150',
          'composer-box',
        )}
        style={{
          padding: '6px 6px 6px 12px',
          background: 'hsl(var(--background))',
          border: `1.5px solid ${isOverLimit ? 'hsl(var(--sienna))' : 'hsl(var(--border))'}`,
        }}
      >
        {isSTTSupported && (
          <button
            type="button"
            onClick={onToggleSTT}
            disabled={disabled || isWaiting}
            aria-label={isListening ? 'Stop recording' : 'Start voice input'}
            title={isListening ? 'Stop recording' : 'Start voice input'}
            className={cn(
              'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            style={
              isListening
                ? {
                    background: 'hsl(var(--sienna) / 0.12)',
                    color: 'hsl(var(--sienna))',
                  }
                : {
                    background: 'transparent',
                    color: 'hsl(var(--muted-foreground))',
                  }
            }
          >
            {isListening ? (
              <Mic className="h-4 w-4 animate-pulse" />
            ) : (
              <MicOff className="h-4 w-4" />
            )}
          </button>
        )}

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={effectivePlaceholder}
          disabled={disabled || isWaiting}
          rows={1}
          aria-label="Your response"
          className={cn(
            'flex-1 resize-none border-0 bg-transparent text-[13px] leading-[1.4] outline-none',
            'placeholder:text-muted-foreground',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
          style={{
            minHeight: 22,
            maxHeight: 120,
            padding: '6px 0',
            color: 'hsl(var(--foreground))',
          }}
        />

        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          aria-label="Send message"
          title="Send message (Enter)"
          className={cn(
            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors',
            'disabled:cursor-not-allowed',
          )}
          style={
            canSubmit
              ? {
                  background: 'hsl(var(--teal))',
                  color: 'hsl(var(--slate-navy))',
                }
              : {
                  background: 'hsl(var(--muted))',
                  color: 'hsl(var(--muted-foreground))',
                }
          }
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Hint line: copy + keyboard shortcuts */}
      <div
        className="mt-1.5 flex items-center justify-between gap-3 px-1 text-[10.5px]"
        style={{ color: 'hsl(var(--muted-foreground))' }}
      >
        <span className="truncate">
          {isWaiting
            ? 'Kaiva is thinking…'
            : 'Answers are stitched into the knowledge canvas in real time.'}
        </span>
        <span className="flex flex-shrink-0 items-center gap-1.5">
          <Kbd>↵</Kbd>
          <span>send</span>
          <span>·</span>
          <Kbd>⇧ ↵</Kbd>
          <span>newline</span>
          {charCount > 0 && (
            <>
              <span>·</span>
              <span
                className={cn('tabular-nums', isOverLimit && 'font-semibold')}
                style={isOverLimit ? { color: 'hsl(var(--sienna))' } : undefined}
              >
                {charCount.toLocaleString()}/{MAX_CHARS.toLocaleString()}
              </span>
            </>
          )}
        </span>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      className="inline-flex items-center justify-center"
      style={{
        fontSize: 10.5,
        padding: '1px 5px',
        borderRadius: 4,
        background: 'hsl(var(--muted))',
        border: '1px solid hsl(var(--border))',
        color: 'hsl(var(--muted-foreground))',
      }}
    >
      {children}
    </kbd>
  );
}
